import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { combineLatest, Subscription, BehaviorSubject } from "rxjs";
import { BreakpointObserver } from "@angular/cdk/layout";
import { map } from "rxjs/operators";
import { UserAliasService } from "@onsip/common/services/api/resources/userAlias/user-alias.service";
import { VoicemailService } from "@onsip/common/services/api/resources/voicemail/voicemail.service";
import {
  GroupAddress,
  GroupAddressService
} from "../../../../common/services/api/resources/groupAddress/group-address.service";
import {
  UserAddressService,
  UserAddress
} from "@onsip/common/services/api/resources/userAddress/user-address.service";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AdvVoicemailboxWithExt } from "./admin-voicemail-types";
import { TranslateService } from "@ngx-translate/core";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { AdminTableAbstractComponent } from "../../abstractClass/admin-table-abstract.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";

const debug = false;

@Component({
  selector: "onsip-admin-voicemail",
  templateUrl: "./admin-voicemail.component.html",
  styleUrls: ["./admin-voicemail.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminVoicemailComponent
  extends AdminTableAbstractComponent<AdvVoicemailboxWithExt>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  E2eLocators = E2eLocators;

  hasVoicemails = new BehaviorSubject<boolean>(true);
  pricingText = "";
  defaultSortingColumn = "mailboxName";

  protected displayedColumnsRecord = {
    XSmall: ["mailboxName", "action"],
    Small: ["mailboxName", "mailboxNumber", "action"],
    Medium: ["mailboxName", "mailboxNumber", "greetings", "action"],
    Large: ["mailboxName", "mailboxNumber", "greetings", "action"],
    XLarge: ["mailboxName", "mailboxNumber", "greetings", "action"]
  };
  protected unsubscriber = new Subscription();

  constructor(
    private voicemailService: VoicemailService,
    private userAliasService: UserAliasService,
    private userAddress: UserAddressService,
    private addressSummaryService: AddressSummaryService,
    private groupAddress: GroupAddressService,
    private dynamicPricing: DynamicPricingService,
    private snackbar: SnackbarService,
    protected dialog: MatDialog,
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected cdr: ChangeDetectorRef
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setDataFilter();
    this.setPricing();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  trackByAdvVoicemailId(index: number, item: AdvVoicemailboxWithExt): string {
    return item.advVoicemailboxId;
  }

  protected getItemValueByColumn(
    item: AdvVoicemailboxWithExt,
    columnName: string
  ): string | number {
    switch (columnName) {
      case "mailboxName":
        return item.name.toLowerCase();
      case "mailboxNumber":
        return item.mailbox;
      case "greetings":
        return item.voicemail.greetings ? 1 : 0;
      default:
        return item.name.toLowerCase();
    }
  }

  protected performDeleteRequest(voicemail: AdvVoicemailboxWithExt): void {
    this.voicemailService.advVoicemailboxDelete(voicemail.advVoicemailboxId).then(result => {
      if (result.status === "success") {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", {
            value: "Voicemail"
          }),
          "success"
        );
      } else {
        this.snackbar.openSnackBar(result.data.message, "error");
        this.voicemailService.clearErrors();
      }
      this.setPricing();
    });
  }

  protected getItemName({ name }: AdvVoicemailboxWithExt): string {
    return name;
  }

  protected initDataSource(): void {
    this.voicemailService.advVoicemailboxBrowseWithOrgId();
    this.userAliasService.userAliasBrowse();
    this.addressSummaryService.addressSummaryBrowse();

    this.unsubscriber.add(
      combineLatest({
        voicemails: this.voicemailService.orgState,
        addresses: this.addressSummaryService.orgState,
        userAlias: this.userAliasService.orgState,
        userAddresses: this.userAddress.orgState,
        groupAddresses: this.groupAddress.orgState
      })
        .pipe(
          map(({ voicemails, addresses, userAlias, userAddresses, groupAddresses }) => {
            return voicemails.map(voicemail => {
              const voicemailboxAddress = addresses.find(
                address => address.addressId === voicemail.addressId
              );
              if (!voicemailboxAddress) {
                return {
                  ...voicemail,
                  voicemailboxAddress: undefined,
                  voicemailboxOwner: "",
                  extensions: userAlias
                    .filter(alias => alias.addressId === voicemail.addressId)
                    .map(alias => alias.aliasUsername)
                };
              }
              // get user or group who is using the current voicemailbox
              const voicemailboxOwner = this.getVoicemailOwner(
                voicemailboxAddress.addressId,
                userAddresses,
                groupAddresses
              );

              return {
                ...voicemail,
                voicemailboxAddress,
                voicemailboxOwner,
                extensions: userAlias
                  .filter(alias => alias.addressId === voicemail.addressId)
                  .map(alias => alias.aliasUsername)
              };
            }) as Array<AdvVoicemailboxWithExt>;
          })
        )
        .subscribe(vmData => {
          this.hasVoicemails.next(vmData.length > 0);
          debug && console.warn(vmData);
          this.dataSource.data = vmData;
          this.isDataObtained$.next(true);
        })
    );
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // mailboxName
      const name = data.name;
      // mailboxNumber
      const mailbox = data.mailbox;
      // greeting
      const greeting = data.voicemail.greetings ? "yes" : "no";

      return (
        name.toLowerCase().includes(filterValue) ||
        mailbox.toLowerCase().includes(filterValue) ||
        greeting.includes(filterValue)
      );
    };
  }

  private setPricing(): void {
    this.dynamicPricing.getDynamicPricing("AdvVoicemailbox", true).then(result => {
      this.pricingText = result;
      this.cdr.markForCheck();
    });
  }

  private getVoicemailOwner(
    addressId: string,
    userAddresses: Array<UserAddress>,
    groupAddresses: Array<GroupAddress>
  ): string {
    const userAddress = userAddresses.find(
      userAddress => userAddress.defaultAddress.addressId === addressId
    );
    if (userAddress) {
      return userAddress.aor;
    } else {
      const group = groupAddresses.find(group => group.defaultAddress?.addressId === addressId);
      if (group) return group.username + "@" + group.address.domain;
    }
    return "";
  }
}
